import { Injectable } from '@angular/core';
import { AppService } from 'app/app.service';
import * as Bluebird from 'bluebird';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStore } from '../storage/storage.service';

export enum SORT_DIRECTION {
    ASC = 'asc',
    DESC = 'desc',
}

export type FilterType = 'costcentre' | 'assetgroup' | 'assetcategory' | 'assettype' | 'name';

export interface TreeListItem {
    id: string;
    name: string;
    parent: string;
}

export interface FilterObject {
    type: FilterType;
    value: string;
}

export interface SortObject {
    value: string;
    direction: SORT_DIRECTION;
}

export interface Filters {
    groups: FilterObject[];
    text: FilterObject;
}

export interface FilterPoint {
    lon: number;
    lat: number;
    address: string;
}

export interface FiltersResponse {
    filters: FilterObject[];
    sort: string;
    limit: number;
    offset: number;
    clientId: string;
    point?: FilterPoint;
}

export interface FilterDictionary {
    [clientId: string]: {
        filter: FilterObject[]
    };
}

@Injectable({
    providedIn: 'root',
})
export class AssetFilterService {

    costCentres: TreeListItem[];
    assetGroups: TreeListItem[];
    assetCategories: TreeListItem[];
    assetTypes: TreeListItem[];

    filters: Filters = {
        text: null,
        groups: [],
    };

    sort: SortObject = {
        value: 'name',
        direction: SORT_DIRECTION.ASC,
    };
    limit = this.app.features.page.overview.pageSize || 300;
    offset = 0;
    clientId: string;
    point: FilterPoint;

    store: LocalStore;

    // listens to any param change be it filter, sort, limit or offset and returns an object with the latest values
    private filtersSubject: BehaviorSubject<FiltersResponse> = new BehaviorSubject(this.getFilters());

    get filters$(): Observable<FiltersResponse> {
        return this.filtersSubject.asObservable();
    }

    private onErrorSubject: BehaviorSubject<Error | string> = new BehaviorSubject(null);

    get onError$(): Observable<Error | string> {
        return this.onErrorSubject.asObservable();
    }

    constructor(private app: AppService) {
        this.store = this.app.getStore('asset-filter');
    }

    loadClient(clientId: string): Bluebird<void> {

        this.clientId = clientId;

        const clients = this.store.get('state') || {};

        if (clients && clients[clientId]) {
            this.filters = { text: clients[clientId].text, groups: clients[clientId].filter };
        } else {
            this.filters = { text: null, groups: [] };
        }

        return Bluebird.all([
            this.app.api.entities.listCostCentres(clientId),
            this.app.api.entities.listAssetCategories(clientId),
            this.app.api.entities.listAssetGroups(clientId),
            this.app.api.entities.listAssetTypes(clientId),
        ]).then(([costCentres, assetCategories, assetGroups, assetTypes]) => {
            this.costCentres = costCentres.items
                .map(({ id, name, parent }) => ({ id, name, parent: parent ? parent.id : 'root' }));

            this.assetCategories = assetCategories.items
                .map(({ id, name, parent }) => ({ id, name, parent: parent ? parent.id : 'root' }));

            this.assetGroups = assetGroups.items
                .map(({ id, name, parent }) => ({ id, name, parent: parent ? parent.id : 'root' }));

            this.assetTypes = assetTypes.items
                .map(({ id, name, parent }) => ({ id, name, parent: parent ? parent.id : 'root' }));

            this.onErrorSubject.next(null);
        }).catch(error => {
            this.onErrorSubject.next(error);
        }).finally(() => {
            this.filtersSubject.next(this.getFilters());
        });
    }

    resetFilterValues() {
        this.filters = {
            groups: [],
            text: null,
        };
        this.sort = {
            value: 'name',
            direction: SORT_DIRECTION.ASC,
        };
        this.offset = 0;

        this.emitChanges();
    }

    setFilter(type: FilterType, value: string) {
        this.offset = 0;
        if (type === 'name') {
            this.filters.text = { type, value };
        } else {
            this.filters.groups = this.filters.groups
                .filter(x => x.type !== type)
                .concat([{ type, value }])
                .filter(x => !!x?.value);
        }

        this.emitChanges();
    }

    setSortValue(value: string) {
        this.sort.value = value;
        this.emitChanges();
    }

    changeSortDirection() {
        if (this.sort.direction === SORT_DIRECTION.ASC) {
            this.sort.direction = SORT_DIRECTION.DESC;
        } else {
            this.sort.direction = SORT_DIRECTION.ASC;
        }

        this.emitChanges();
    }

    setLimit(value: number) {
        this.limit = value;
        this.emitChanges();
    }

    setOffset(value: number) {
        this.offset = value;
        this.emitChanges();
    }

    setPoint(value: FilterPoint) {
        this.point = value;
        this.emitChanges();
    }

    // return all necessary params to be used in an asset request
    getFilters(): FiltersResponse {
        return {
            limit: this.limit,
            sort: this.getSortString(),
            offset: this.offset,
            clientId: this.clientId,
            filters: [this.filters.text, ...this.filters.groups].filter(x => !!x && !!x.value),
            point: this.point,
        };
    }

    getFilterValueByType(type: string): string {
        const match = [this.filters.text, ...this.filters.groups]
            .filter(x => !!x)
            .find(f => f.type === type);

        return match ? match.value : null;
    }

    getSortString(): string {
        return `${this.sort.value}:${this.sort.direction}`;
    }

    getFilterRqlString(): string {
        const fmt = (type: FilterType, value: string) => {
            if (type === 'name') {
                return `${type}=*${value.replace(/[^a-z0-9\ ]/ig, '').replace(/ +/g, '*')}*`;
            } else {
                switch (type) {
                    case 'assetcategory': return 'categories.id=' + value + '/*';
                    case 'assetgroup': return 'groups.id=' + value + '/*';
                    case 'costcentre': return 'accessGroup.id=' + value + '/*';
                    case 'assettype': return 'assetType.id=' + value + '/*';
                }
            }
        };

        let str = 'state=active';
        this.getFilters().filters.forEach(filter => str += ',' + fmt(filter.type, filter.value));
        return str;
    }

    emitChanges() {
        this.storeClientFilters(this.clientId);
        this.filtersSubject.next(this.getFilters());
    }

    storeClientFilters(clientId: string) {
        const clients = this.store.get('state') || {};

        clients[clientId] = { text: this.filters.text, filter: this.filters.groups || [] };

        try {
            this.store.set('state', clients);
        } catch (error) {
            console.error(error);
        }
    }
}
